/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { ArrowLeft, FileText } from "lucide-react";
import { useEffect } from "react";

interface TermsOfServiceProps {
  onBack: () => void;
}

export function TermsOfService({ onBack }: TermsOfServiceProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <img src="/docgether.png" alt="DocGether" className="h-8 w-auto rounded-lg" />
          
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-zinc-100 rounded-xl">
            <FileText className="w-6 h-6 text-zinc-700" />
          </div>
          <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight">
            Terms of Service
          </h1>
        </div>

        <div className="prose prose-zinc max-w-none">
          <p className="text-zinc-500 text-lg leading-relaxed">
            Welcome to DocGether. By using our service, you agree to these Terms of Service. 
            Please read them carefully.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            1. Acceptance of Terms
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            By accessing or using DocGether, you agree to be bound by these Terms of Service. 
            If you disagree with any part of the terms, you may not access the service. These 
            terms apply to all visitors, users, and others who access or use the service.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            2. Description of Service
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            DocGether is a free service that allows users to upload OpenAPI and Swagger 
            specifications to generate interactive API documentation. The service includes 
            features for testing API endpoints, sharing documentation, and managing API specs.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            3. User Accounts
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-4">
            To use certain features of the service, you must create an account. You are responsible for:
          </p>
          <ul className="space-y-2 text-zinc-600 mb-6">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Maintaining the confidentiality of your account credentials</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>All activities that occur under your account</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Notifying us immediately of any unauthorized access</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Ensuring your account information is accurate and up-to-date</span>
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            4. Acceptable Use
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-4">
            You agree not to use the service to:
          </p>
          <ul className="space-y-2 text-zinc-600 mb-6">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Upload malicious content, malware, or harmful code</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Violate any applicable laws or regulations</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Infringe on intellectual property rights of others</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Attempt to gain unauthorized access to the service</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Interfere with or disrupt the service or servers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Upload API specifications you do not have rights to share</span>
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            5. Content Ownership
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            You retain all rights to the API specifications you upload. By uploading content, 
            you grant DocGether a limited license to store, display, and process your 
            specifications solely for the purpose of providing the service. We do not claim 
            ownership of your content.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            6. Service Availability
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            DocGether is provided "as is" and "as available." While we strive to maintain 
            high availability, we do not guarantee uninterrupted access to the service. 
            We reserve the right to modify, suspend, or discontinue the service at any time 
            without notice.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            7. Limitation of Liability
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            To the maximum extent permitted by law, DocGether and its operators shall not be 
            liable for any indirect, incidental, special, consequential, or punitive damages 
            resulting from your use or inability to use the service.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            8. Changes to Terms
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            We reserve the right to modify these terms at any time. We will notify users of 
            significant changes by posting the updated terms on this page. Your continued use 
            of the service after such changes constitutes acceptance of the new terms.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            9. Termination
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            We may terminate or suspend your account and access to the service immediately, 
            without prior notice, for any reason, including breach of these Terms. Upon 
            termination, your right to use the service will cease immediately.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            10. Contact Information
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            If you have any questions about these Terms of Service, please contact us at{" "}
            <a href="mailto:legal@docgether.io" className="text-zinc-900 hover:underline">
              legal@docgether.io
            </a>.
          </p>

          <div className="mt-12 p-6 bg-zinc-50 rounded-2xl border border-zinc-200">
            <p className="text-sm text-zinc-500">
              <strong>Last Updated:</strong> March 2025<br />
              By using DocGether, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
